import type { FilterObject } from '@moment/trading-types/filter-expression'
import type {
  GlobalConstraints,
  Sleeve,
} from '@/portfolio/proposal-builder/types'
import type {
  ModelConstraint,
  UniverseConstraint,
} from '@/types/api/constraints'

import { UniverseConstraintType } from '@moment/horizon-data-access/proposal'
import { BondStatus, Currency } from '@moment/trading-types/reference-data'

import {
  BOND_STATUS_CONSTRAINT_ID,
  GLOBAL_CURRENCY_CONSTRAINT_ID,
  LEGACY_GLOBAL_CURRENCY_CONSTRAINT_ID,
  STRUCTURED_PRODUCT_CONSTRAINT_ID,
} from '@/portfolio/proposal-builder/constants'
import { stripNoOpScope } from '@/portfolio/shared/constraint/no-op-scope'

// System-managed global constraints (currency, bond-status, structured-product)
// share the `global` id marker and are owned by the backend.
export function filterGlobalConstraints(
  constraints: ModelConstraint[],
): string[] {
  return constraints
    .map((c) => c.id)
    .filter((id): id is string => !!id && id.includes('global'))
}

export function buildGlobalUniverseConstraint(
  currency: Currency = Currency.USD,
): UniverseConstraint {
  return {
    id: GLOBAL_CURRENCY_CONSTRAINT_ID,
    type: UniverseConstraintType.universe,
    filter: { currency: { in: [currency] } },
  }
}

// Structural view of a constraint, so this reads both the hand-written
// ModelConstraint union and the generated one off a model strategy.
interface ConstraintLike {
  id?: string
  type?: string
  scope?: unknown
  filter?: unknown
}

function parseCurrency(value: unknown): Currency | null {
  return typeof value === 'string' &&
    Object.values(Currency).includes(value as Currency)
    ? (value as Currency)
    : null
}

// A wider constraint — scoped, sibling clauses, several currencies — is a real
// rule, so it reports null rather than one of its currencies as *the* currency.
export function readCurrencyConstraint(
  constraint: ConstraintLike | null | undefined,
): Currency | null {
  if (constraint?.type !== UniverseConstraintType.universe) return null
  // Reads `constraint.scope` directly rather than through extractScope, so the
  // strip has to happen here for a legacy constraint carrying a no-op clause.
  if (stripNoOpScope(constraint.scope as FilterObject | null | undefined)) {
    return null
  }
  const filter = constraint.filter as Record<string, unknown> | undefined
  if (!filter) return null
  // Absent compound clauses serialize as empty arrays; ignore those keys.
  const keys = Object.keys(filter).filter(
    (key) =>
      !(key === 'and' || key === 'or') ||
      (Array.isArray(filter[key]) && filter[key].length > 0),
  )
  if (keys.length !== 1 || keys[0] !== 'currency') return null
  const inList = (filter.currency as { in?: unknown[] } | undefined)?.in
  if (!Array.isArray(inList) || inList.length !== 1) return null
  return parseCurrency(inList[0])
}

// A model has no currency field: it lives in a system-managed universe
// constraint, read back out of the constraint list at format time.
export function extractCurrencyFromConstraints(
  constraints: readonly (ConstraintLike | null | undefined)[] = [],
): Currency | null {
  return readCurrencyConstraint(
    constraints.find(
      (c) =>
        c?.id === GLOBAL_CURRENCY_CONSTRAINT_ID ||
        c?.id === LEGACY_GLOBAL_CURRENCY_CONSTRAINT_ID,
    ),
  )
}

// A sleeve's currency is not stored: it is read back out of the sleeve's own
// currency constraint, the form the API and the optimizer round-trip.
export function getSleeveCurrency(sleeve: Sleeve | undefined): Currency | null {
  return readCurrencyConstraint(
    sleeve?.state.globalConstraints.currencyConstraint,
  )
}

export function createDefaultGlobalConstraints(
  currency: Currency = Currency.USD,
): GlobalConstraints {
  return {
    bondStatusConstraint: {
      id: BOND_STATUS_CONSTRAINT_ID,
      type: UniverseConstraintType.universe,
      filter: {
        status: { in: [BondStatus.Outstanding, BondStatus.PreIssuance] },
      },
    },
    structuredProductConstraint: {
      id: STRUCTURED_PRODUCT_CONSTRAINT_ID,
      type: UniverseConstraintType.universe,
      filter: { is_structured_product: { not_equal_to: true } },
    },
    currencyConstraint: buildGlobalUniverseConstraint(currency),
  }
}
